import React from 'react';
import { motion, AnimatePresence, type Variants, type Transition } from 'framer-motion';
import type { Incident } from '../types';
import { formatDateTime, formatDuration, getSeverityLabel } from '../utils';
import { INCIDENT_TYPE_CONFIG } from '../constants';

interface IncidentDetailsProps {
    incident: Incident;
    isOpen: boolean;
}

const springTransition: Transition = {
    type: "spring",
    stiffness: 300,
    damping: 30
};

const detailsVariants: Variants = {
    hidden: { 
        opacity: 0, 
        height: 0 
    },
    visible: { 
        opacity: 1, 
        height: "auto",
        transition: springTransition
    },
    exit: { 
        opacity: 0, 
        height: 0,
        transition: springTransition
    }
};

export const IncidentDetails: React.FC<IncidentDetailsProps> = ({
    incident,
    isOpen
}) => {
    const typeConfig = INCIDENT_TYPE_CONFIG[incident.type];

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    variants={detailsVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="overflow-hidden"
                >
                    <div className="px-4 pb-4 pt-2 bg-[var(--bg)] border-t border-[var(--border)]">
                        <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mb-4">
                            {incident.description}
                        </p>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            <DetailItem label="Сайт" value={incident.meta.siteName} />
                            <DetailItem 
                                label="URL" 
                                value={
                                    <a 
                                        href={incident.meta.siteUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-[var(--brand)] hover:underline truncate block"
                                    >
                                        {incident.meta.siteUrl.replace(/^https?:\/\//, '')}
                                    </a>
                                } 
                            />
                            <DetailItem 
                                label="Важность" 
                                value={getSeverityLabel(incident.severity)} 
                            />
                            <DetailItem 
                                label="Регион" 
                                value={incident.meta.region || '—'} 
                            />
                            {incident.meta.errorCode && (
                                <DetailItem label="Код ошибки" value={incident.meta.errorCode} />
                            )}
                            {incident.meta.affectedUsers && (
                                <DetailItem 
                                    label="Пользователей" 
                                    value={incident.meta.affectedUsers.toLocaleString('ru-RU')} 
                                />
                            )}
                            {incident.meta.duration && (
                                <DetailItem 
                                    label="Длительность" 
                                    value={formatDuration(incident.meta.duration)} 
                                />
                            )}
                            <DetailItem 
                                label="Назначен" 
                                value={incident.assignedTo || 'Не назначен'} 
                            />
                        </div>

                        <div className="mt-4">
                            <h4 className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">
                                Хронология событий
                            </h4>
                            <div className="space-y-3">
                                {incident.timeline.map((event, idx) => (
                                    <TimelineItem 
                                        key={event.id} 
                                        event={event} 
                                        isLast={idx === incident.timeline.length - 1}
                                        color={typeConfig.color}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[var(--border)]">
                            {incident.status !== 'resolved' && (
                                <>
                                    <button 
                                        type="button"
                                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--status-up-bg)] text-[var(--status-up-text)] hover:opacity-80 transition-opacity"
                                    >
                                        Отметить устранённым
                                    </button>
                                    <button 
                                        type="button"
                                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text)] transition-colors"
                                    >
                                        Назначить исполнителя
                                    </button>
                                </>
                            )}
                            <button 
                                type="button"
                                className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
                            >
                                Добавить комментарий
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({
    label,
    value
}) => (
    <div>
        <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">
            {label}
        </div>
        <div className="text-[12px] text-[var(--text)] font-medium truncate">
            {value}
        </div>
    </div>
);

interface TimelineItemProps {
    event: Incident['timeline'][0];
    isLast: boolean;
    color: string;
}

const TimelineItem: React.FC<TimelineItemProps> = ({ event, isLast, color }) => {
    const icons: Record<string, React.ReactNode> = {
        created: (
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="16"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
        ),
        status_changed: (
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
        ),
        resolved: (
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12"/>
            </svg>
        ),
        comment: (
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
        )
    };

    return (
        <div className="flex items-start gap-3">
            <div className="flex flex-col items-center">
                <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white"
                    style={{ backgroundColor: color }}
                >
                    {icons[event.type] || icons.comment}
                </div>
                {!isLast && (
                    <div className="w-0.5 h-full min-h-[24px] bg-[var(--border)] mt-1" />
                )}
            </div>
            <div className="flex-1 pb-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[12px] font-medium text-[var(--text)]">
                        {event.message}
                    </span>
                    <span className="text-[10px] text-[var(--text-muted)] font-mono">
                        {formatDateTime(event.timestamp)}
                    </span>
                </div>
                {event.author && (
                    <div className="text-[11px] text-[var(--text-muted)]">
                        {event.author}
                    </div>
                )}
            </div>
        </div>
    );
};